import {
  Button,
  CloseIcon,
  Modal,
  RealEstatePaymentDetailsProps,
  RealEstatePaymentDetailsSchema,
  Select,
  TextArea,
  TextInput
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useEffect } from "react";
import { UseFormReset, useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitPaymentDetails: (
    values: RealEstatePaymentDetailsProps,
    reset: UseFormReset<RealEstatePaymentDetailsProps>
  ) => void;
  remove: (id: number) => void;
  editData?: Partial<RealEstatePaymentDetailsProps> & { id: number };
}

const defaultValues = {
  payment_to: "",
  other_person_entity: "",
  value: "",
  description: ""
};

export default function PaymentDetails({ isOpen, onClose, onSubmitPaymentDetails, remove, editData }: IProps) {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const { handleSubmit, control, watch, reset, resetField } = useForm<RealEstatePaymentDetailsProps>({
    mode: "all",
    defaultValues,
    resolver: yupResolver(RealEstatePaymentDetailsSchema)
  });

  const paymentToOptions = [
    { label: `You (${initialDetails?.user?.first_name})`, value: "user" },
    { label: `${initialDetails?.spouse?.first_name}`, value: "spouse" },
    {
      label: "Someone Else",
      value: "something_else"
    }
  ];
  const isDeleting = false;
  const isLoading = false;

  const onDelete = () => {
    if (editData) {
      remove(editData.id);
    }
    onClose();
    reset(defaultValues);
  };

  useEffect(() => {
    if (editData) reset(editData);
    else reset(defaultValues);
  }, [editData]);

  return (
    <Modal title="Add Payment Details" isOpen={isOpen} closeModal={onClose}>
      <form
        id="payment-details-form"
        className="mt-5 space-y-5"
        onSubmit={handleSubmit(data => onSubmitPaymentDetails(data, reset))}
      >
        <Select
          title="Payment to"
          control={control}
          name="payment_to"
          id="payment_to"
          placeholder="Select Payee"
          options={paymentToOptions}
          onChange={val => {
            if (val !== "something_else") {
              resetField("other_person_entity", { defaultValue: "" });
            }
          }}
          className="w-full lg:w-1/2"
        />
        {watch("payment_to") === "something_else" ? (
          <TextInput
            title="Name of Other Person or Entity"
            control={control}
            name="other_person_entity"
            id="other_person_entity"
          />
        ) : null}

        <TextArea
          title="Description of Payment"
          control={control}
          name="description"
          id="description"
          placeholder="Description"
        />

        <TextInput
          title="Amount"
          control={control}
          name="value"
          id="value"
          placeholder="eg $500,000"
          isAmount
          wrapClasses="w-full lg:w-1/2"
        />

        <div
          className={classNames("flex justify-end items-center w-full mt-7", {
            "justify-between": !!editData?.id
          })}
        >
          {typeof editData?.id === "number" ? (
            <Button
              className="px-5 py-2 flex justify-center items-center group"
              type="button"
              // btnType="danger"
              // appearance="outline"
              onClick={onDelete}
            >
              <CloseIcon classes="fill-current text-red-500 group-hover:text-white mr-2" width={20} height={20} />
              <p>DELETE</p>
              {isDeleting ? <Oval width={14} height={14} color="#fff" strokeWidth={5} wrapperClass="ml-2" /> : null}
            </Button>
          ) : null}

          <Button className="px-12 py-2" type="submit">
            <p>Save</p>
            {isLoading ? <Oval width={14} height={14} color="#fff" wrapperClass="ml-2" strokeWidth={5} /> : null}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
